import { Fragment } from 'react';
import styled from 'styled-components';
import { CashFlowsByNature, GroupedCashFlows } from '@algoan/dashboard/model';
import { useSelector } from '@algoan/dashboard/store/store';
import { CashFlowsTableStyle } from './style';
import { FormattedMoney } from '../atomic-components';
import {
  sumCategoryForMonth,
  sumTotalForCategory,
  getEveryCashFlowsFromCashFlowCollection,
} from '@algoan/dashboard/calculators';
import { CashFlowRow } from './cash-flow-row';
import { isCashFlowCollection } from '@algoan/dashboard/utils';
import { CashFlowCollection } from './cash-flow-collection';
import { checkedCashFlowsSelector } from '@algoan/dashboard/store/selectors';

export interface CashFlowNatureProps {
  calendar: GroupedCashFlows['calendar'];
  cashFlowsByNature: CashFlowsByNature;
  checkable: boolean;
  displayUndefinedAsZero?: boolean;
  firstColumnLabel?: React.ReactNode;
  title: string;
}

export const CashFlowNature: React.FC<CashFlowNatureProps> = ({
  calendar,
  cashFlowsByNature,
  checkable,
  firstColumnLabel,
  title,
}) => {
  const checkedRows = useSelector(checkedCashFlowsSelector);

  return (
    <>
      <CashFlowsTableStyle.BodyHeader>
        <th>{title}</th>
        <th>
          <S.SectionHeading>{firstColumnLabel}</S.SectionHeading>
        </th>
        {calendar.map((date, index) => (
          <th key={`category-${title}-${index}`}>
            <FormattedMoney
              number={Math.abs(
                sumCategoryForMonth(
                  date,
                  getEveryCashFlowsFromCashFlowCollection(cashFlowsByNature.cashFlows),
                  checkedRows,
                ),
              )}
            />
          </th>
        ))}
        <th>
          <FormattedMoney
            number={Math.abs(
              sumTotalForCategory(getEveryCashFlowsFromCashFlowCollection(cashFlowsByNature.cashFlows), checkedRows),
            )}
          />
        </th>
      </CashFlowsTableStyle.BodyHeader>
      {cashFlowsByNature.cashFlows.map((cashFlow, index) => {
        return (
          <Fragment key={`cash-flow-${title}-${index}`}>
            {isCashFlowCollection(cashFlow) ? (
              <CashFlowCollection
                calendar={calendar}
                cashFlowCollection={cashFlow}
                checkable={checkable}
                headerExists={!!firstColumnLabel}
                isEven={index % 2 === 0}
                dataTestId={`cash-flow-${title}-${index}`}
              />
            ) : (
              <CashFlowRow
                calendar={calendar}
                cashFlow={cashFlow}
                checkable={checkable}
                headerExists={!!firstColumnLabel}
                isEven={index % 2 === 0}
                dataTestId={`cash-flow-${title}-${index}`}
              />
            )}
          </Fragment>
        );
      })}
    </>
  );
};

const S = {
  SectionHeading: styled.div`
    font-size: 9px;
    height: 25px;
    font-weight: bold;
    display: flex;
    justify-content: center;
    align-items: center;
    text-align: center;
  `,
};
